import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  getInbox,
  getCommunicationStats,
  getTemplates,
  createTemplate,
  getAutomationRules,
  createAutomationRule,
} from '../../api/communicationsHub';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const STATUS_OPTIONS = ['', 'Pending', 'Sent', 'Delivered', 'Read', 'Failed'];
const CHANNEL_OPTIONS = ['', 'WhatsApp', 'SMS', 'Email', 'Call'];

const CommunicationsInbox = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [templateForm, setTemplateForm] = useState({
    code: '',
    name: '',
    channel: 'WhatsApp',
    body: '',
    subject: '',
  });
  const [ruleForm, setRuleForm] = useState({
    name: '',
    eventKey: 'appointment.completed',
    channels: ['WhatsApp'],
    templateCode: '',
    active: true,
    createFollowUpTask: false,
  });
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    q: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [inboxRes, statsRes] = await Promise.all([
        getInbox(filters),
        getCommunicationStats(),
      ]);
      setRows(inboxRes?.data || []);
      setStats(statsRes?.data || null);
      setError('');
    } catch (err) {
      const message = getApiErrorMessage(err, formatFailedActionMessage('load', 'communications inbox'));
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfigData = async () => {
    try {
      const [templateRes, ruleRes] = await Promise.all([
        getTemplates(),
        getAutomationRules(),
      ]);
      setTemplates(templateRes?.data || []);
      setRules(ruleRes?.data || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('load', 'communication configuration')));
    }
  };

  useEffect(() => {
    loadConfigData();
  }, []);

  const totalMessages = useMemo(() => rows.length, [rows]);

  const handleCreateTemplate = async () => {
    try {
      await createTemplate(templateForm);
      toast.success('Template created');
      setTemplateForm({ code: '', name: '', channel: 'WhatsApp', body: '', subject: '' });
      loadConfigData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('create', 'template')));
    }
  };

  const handleCreateRule = async () => {
    try {
      await createAutomationRule(ruleForm);
      toast.success('Automation rule created');
      setRuleForm({
        name: '',
        eventKey: 'appointment.completed',
        channels: ['WhatsApp'],
        templateCode: '',
        active: true,
        createFollowUpTask: false,
      });
      loadConfigData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('create', 'automation rule')));
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Communications Inbox
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search message"
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option || 'all'} value={option}>
                  {option || 'All'}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Channel"
              value={filters.channel}
              onChange={(e) => setFilters((prev) => ({ ...prev, channel: e.target.value }))}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option || 'all'} value={option}>
                  {option || 'All'}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={loadData} disabled={loading}>
              Apply Filters
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Total loaded: {totalMessages}
            </Typography>
            {stats && (
              <Typography variant="body2" color="text.secondary">
                Delivered: {stats?.statusStats?.Delivered || 0} | Failed: {stats?.statusStats?.Failed || 0}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.sentAt ? new Date(row.sentAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>{row.patient?.name || '—'}</TableCell>
                    <TableCell>{row.channel}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} />
                    </TableCell>
                    <TableCell>{row.provider || 'mock'}</TableCell>
                    <TableCell>{Number(row?.cost?.amount || 0).toFixed(2)} {row?.cost?.currency || 'USD'}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.message}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No communications found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Templates</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="Code" value={templateForm.code} onChange={(e) => setTemplateForm((p) => ({ ...p, code: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="Name" value={templateForm.name} onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Channel"
              value={templateForm.channel}
              onChange={(e) => setTemplateForm((p) => ({ ...p, channel: e.target.value }))}
            >
              {CHANNEL_OPTIONS.filter(Boolean).map((channel) => (
                <MenuItem key={channel} value={channel}>{channel}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="Subject" value={templateForm.subject} onChange={(e) => setTemplateForm((p) => ({ ...p, subject: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Body" value={templateForm.body} onChange={(e) => setTemplateForm((p) => ({ ...p, body: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button fullWidth variant="contained" onClick={handleCreateTemplate}>Add</Button>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Active templates: {templates.length}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>Automation Rules</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Rule name" value={ruleForm.name} onChange={(e) => setRuleForm((p) => ({ ...p, name: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Event key" value={ruleForm.eventKey} onChange={(e) => setRuleForm((p) => ({ ...p, eventKey: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Template"
              value={ruleForm.templateCode}
              onChange={(e) => setRuleForm((p) => ({ ...p, templateCode: e.target.value }))}
            >
              <MenuItem value="">None</MenuItem>
              {templates.map((template) => (
                <MenuItem key={template._id} value={template.code}>
                  {template.name} ({template.code})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Channel"
              value={ruleForm.channels[0]}
              onChange={(e) => setRuleForm((p) => ({ ...p, channels: [e.target.value] }))}
            >
              {CHANNEL_OPTIONS.filter(Boolean).map((channel) => (
                <MenuItem key={channel} value={channel}>{channel}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button fullWidth variant="contained" onClick={handleCreateRule}>Add</Button>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Active rules: {rules.length}
        </Typography>
      </Paper>
    </Box>
  );
};

export default CommunicationsInbox;
